import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import Navbar from '../components/Navbar';
import {
  AnnouncementTheme,
  SduiPage,
  CreateAnnouncementPayload,
  EventConfig,
  EventMilestone,
  createAnnouncement,
  updateAnnouncement,
  fetchAnnouncementDetail,
  fetchAnnouncementAnalytics,
  ANNOUNCEMENT_TYPES,
  AUDIENCE_OPTIONS,
  PLATFORM_OPTIONS,
  WIDGET_TYPES,
  AnnouncementType,
  AnnouncementAudience,
  Platform,
} from '../services/announcementApi';

// ============================================================
// Page Templates
// ============================================================
const PAGE_TEMPLATES: Record<string, { label: string; page: SduiPage }> = {
  hero: {
    label: 'Hero Page',
    page: {
      backgroundColor: '#0A0A0A',
      backgroundImageUrl: null,
      scrollable: true,
      navLabel: 'Start Journey',
      widgets: [
        { type: 'spacer', props: { height: 28 } },
        { type: 'image', props: { url: '', height: 190, fit: 'contain' } },
        { type: 'spacer', props: { height: 20 } },
        { type: 'text', props: { text: 'Event Title', fontFamily: 'Cormorant Garamond', fontSize: 28, fontWeight: 'w600', color: '#C9A227', textAlign: 'center', lineHeight: 1.2 } },
        { type: 'spacer', props: { height: 14 } },
        { type: 'text', props: { text: 'Description line 1', fontFamily: 'Poppins', fontSize: 14, fontWeight: 'w400', color: '#FFFFFF', textAlign: 'center' } },
        { type: 'spacer', props: { height: 24 } },
      ],
    },
  },
  step: {
    label: 'Step Page',
    page: {
      backgroundColor: '#0A0A0A',
      backgroundImageUrl: null,
      scrollable: true,
      navLabel: 'Next →',
      widgets: [
        { type: 'spacer', props: { height: 20 } },
        { type: 'container', props: { padding: { top: 10, bottom: 10, left: 16, right: 16 }, color: '#C9A227', borderRadius: 18 }, children: [
          { type: 'text', props: { text: 'STEP 1 OF 4', fontFamily: 'Poppins', fontSize: 10, fontWeight: 'w700', color: '#FFFFFF', textAlign: 'center' } },
        ]},
        { type: 'spacer', props: { height: 12 } },
        { type: 'text', props: { text: 'Step Title', fontFamily: 'Cormorant Garamond', fontSize: 28, fontWeight: 'w600', color: '#FFFFFF', textAlign: 'center', lineHeight: 1.2 } },
        { type: 'text', props: { text: 'Step subtitle', fontFamily: 'Poppins', fontSize: 14, fontWeight: 'w400', color: '#8E8E93', textAlign: 'center' } },
        { type: 'spacer', props: { height: 16 } },
      ],
    },
  },
  rewards: {
    label: 'Rewards Page',
    page: {
      backgroundColor: '#0A0A0A',
      backgroundImageUrl: null,
      scrollable: true,
      navLabel: 'ENTER EVENT',
      widgets: [
        { type: 'spacer', props: { height: 18 } },
        { type: 'icon', props: { name: 'crown', size: 54, color: '#C9A227' } },
        { type: 'spacer', props: { height: 10 } },
        { type: 'text', props: { text: 'FINAL REWARDS', fontFamily: 'Cormorant Garamond', fontSize: 28, fontWeight: 'w600', color: '#FFFFFF', textAlign: 'center' } },
        { type: 'spacer', props: { height: 12 } },
        { type: 'text', props: { text: 'Complete the event to earn all rewards', fontFamily: 'Poppins', fontSize: 14, fontWeight: 'w400', color: '#8E8E93', textAlign: 'center' } },
        { type: 'spacer', props: { height: 16 } },
      ],
    },
  },
};

// ============================================================
// Validation
// ============================================================
function validatePayload(data: Partial<CreateAnnouncementPayload>): string[] {
  const errors: string[] = [];
  if (!data.slug || !/^[a-z0-9-]+$/.test(data.slug)) errors.push('Slug must be lowercase alphanumeric with hyphens only');
  if (!data.title || data.title.length > 200) errors.push('Title is required (max 200 chars)');
  if (!data.thumbnailUrl) errors.push('Thumbnail URL is required');
  if (!data.startAt) errors.push('Start date is required');
  if (!data.pages || data.pages.length === 0) errors.push('At least one page is required');
  if (data.pages) {
    data.pages.forEach((p, i) => {
      if (!p.widgets || p.widgets.length === 0) errors.push(`Page ${i + 1} must have at least one widget`);
    });
  }
  if (data.priority !== undefined && (data.priority < 0 || data.priority > 100)) errors.push('Priority must be 0-100');
  return errors;
}

function validatePagesJson(json: string): { pages: SduiPage[] | null; error: string | null } {
  try {
    const parsed = JSON.parse(json);
    if (!Array.isArray(parsed)) return { pages: null, error: 'Pages must be a JSON array' };
    if (parsed.length === 0) return { pages: null, error: 'At least one page is required' };
    for (let i = 0; i < parsed.length; i++) {
      const p = parsed[i];
      if (!p.widgets || !Array.isArray(p.widgets)) return { pages: null, error: `Page ${i + 1}: widgets must be an array` };
      if (p.widgets.length === 0) return { pages: null, error: `Page ${i + 1}: must have at least one widget` };
      for (let j = 0; j < p.widgets.length; j++) {
        const w = p.widgets[j];
        if (!w.type || typeof w.type !== 'string') return { pages: null, error: `Page ${i + 1}, Widget ${j + 1}: type is required` };
      }
    }
    return { pages: parsed, error: null };
  } catch (e: any) {
    return { pages: null, error: `Invalid JSON: ${e.message}` };
  }
}

// ============================================================
// Component
// ============================================================
const AnnouncementEditor: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);

  // Form state
  const [slug, setSlug] = useState('');
  const [type, setType] = useState<AnnouncementType>('EVENT');
  const [title, setTitle] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [thumbnailUrl, setThumbnailUrl] = useState('');
  const [badgeText, setBadgeText] = useState('');
  const [priority, setPriority] = useState(0);
  const [startAt, setStartAt] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [targetAudience, setTargetAudience] = useState<AnnouncementAudience>('ALL');
  const [targetPlatforms, setTargetPlatforms] = useState<Platform[]>(['all']);
  const [status, setStatus] = useState<string>('DRAFT');

  // Theme
  const [themePrimary, setThemePrimary] = useState('#C9A227');
  const [themeSecondary, setThemeSecondary] = useState('#FFFFFF');
  const [themeText, setThemeText] = useState('#FFFFFF');
  const [themeBg, setThemeBg] = useState('#0A0A0A');
  const [themeFont, setThemeFont] = useState('Poppins');

  // Event Config
  const [ecEnabled, setEcEnabled] = useState(false);
  const [ecCategoryKey, setEcCategoryKey] = useState('');
  const [ecMilestones, setEcMilestones] = useState<EventMilestone[]>([]);
  const [ecBadgeName, setEcBadgeName] = useState('');
  const [ecBadgeImageUrl, setEcBadgeImageUrl] = useState('');
  const [ecBadgeDescription, setEcBadgeDescription] = useState('');

  // Pages JSON editor
  const [pagesJson, setPagesJson] = useState('[]');
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [jsonValid, setJsonValid] = useState(false);

  // UI state
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [analytics, setAnalytics] = useState<{ eventType: string; count: number; uniqueUsers: number }[] | null>(null);
  const [activeTab, setActiveTab] = useState<'metadata' | 'pages' | 'theme' | 'event-config' | 'analytics'>('metadata');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Load existing announcement for edit
  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const doc = await fetchAnnouncementDetail(id);
        setTitle(doc.title || '');
        setSubtitle(doc.subtitle || '');
        if (doc.pages) {
          setPagesJson(JSON.stringify(doc.pages, null, 2));
          setJsonValid(true);
        }
        if (doc.theme) {
          setThemePrimary(doc.theme.primaryColor || '#C9A227');
          setThemeSecondary(doc.theme.secondaryColor || '#FFFFFF');
          setThemeText(doc.theme.textColor || '#FFFFFF');
          setThemeBg(doc.theme.backgroundColor || '#0A0A0A');
          setThemeFont(doc.theme.fontFamily || 'Poppins');
        }
        // Load eventConfig from detail response
        if ((doc as any).eventConfig) {
          const ec = (doc as any).eventConfig;
          setEcEnabled(true);
          setEcCategoryKey(ec.categoryKey || '');
          setEcMilestones(ec.milestones || []);
          setEcBadgeName(ec.badge?.name || '');
          setEcBadgeImageUrl(ec.badge?.imageUrl || '');
          setEcBadgeDescription(ec.badge?.description || '');
        }
        // Fetch full doc with metadata (the detail endpoint only returns title/pages/theme)
        // We need another call or use list endpoint
        const { fetchAnnouncements: fetchList } = await import('../services/announcementApi');
        const listRes = await fetchList({ limit: 100 });
        const full = listRes.data.find((a) => a._id === id);
        if (full) {
          setSlug(full.slug);
          setType(full.type);
          setThumbnailUrl(full.thumbnailUrl);
          setBadgeText(full.badgeText || '');
          setPriority(full.priority);
          setStartAt(full.startAt ? full.startAt.slice(0, 16) : '');
          setExpiresAt(full.expiresAt ? full.expiresAt.slice(0, 16) : '');
          setTargetAudience(full.targetAudience);
          setTargetPlatforms(full.targetPlatforms ?? ['all']);
          setStatus(full.status);
          // Also check eventConfig from list response (fallback)
          if ((full as any).eventConfig) {
            const ec = (full as any).eventConfig;
            setEcEnabled(true);
            setEcCategoryKey(ec.categoryKey || '');
            setEcMilestones(ec.milestones || []);
            setEcBadgeName(ec.badge?.name || '');
            setEcBadgeImageUrl(ec.badge?.imageUrl || '');
            setEcBadgeDescription(ec.badge?.description || '');
          }
        }
        // Fetch analytics
        const stats = await fetchAnnouncementAnalytics(id);
        setAnalytics(stats);
      } catch (err: any) {
        toast.error(err.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  // Validate JSON on change
  useEffect(() => {
    const { pages, error } = validatePagesJson(pagesJson);
    setJsonError(error);
    setJsonValid(pages !== null);
  }, [pagesJson]);

  // Auto-generate slug from title
  const autoSlug = () => {
    if (!isEdit && title) {
      setSlug(title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, ''));
    }
  };

  const handleSave = async (newStatus?: string) => {
    const { pages } = validatePagesJson(pagesJson);
    if (!pages) {
      toast.error('Fix JSON errors before saving');
      return;
    }

    const theme: AnnouncementTheme = {
      primaryColor: themePrimary,
      secondaryColor: themeSecondary,
      textColor: themeText,
      backgroundColor: themeBg,
      fontFamily: themeFont,
    };

    let eventConfig: EventConfig | null = null;
    if (ecEnabled && ecCategoryKey) {
      eventConfig = { categoryKey: ecCategoryKey };
      if (ecMilestones.length > 0) {
        eventConfig.milestones = ecMilestones.filter((m) => m.label && m.imageUrl);
      }
      if (ecBadgeName && ecBadgeImageUrl) {
        eventConfig.badge = {
          name: ecBadgeName,
          imageUrl: ecBadgeImageUrl,
          description: ecBadgeDescription || undefined,
        };
      }
    }

    const payload: any = {
      slug,
      type,
      title,
      subtitle: subtitle || undefined,
      thumbnailUrl,
      badgeText: badgeText || null,
      priority,
      startAt: new Date(startAt).toISOString(),
      expiresAt: expiresAt ? new Date(expiresAt).toISOString() : null,
      targetAudience,
      targetPlatforms,
      pages,
      theme,
      eventConfig,
    };

    if (newStatus) {
      payload.status = newStatus;
    }

    const errors = validatePayload(payload);
    if (errors.length > 0) {
      errors.forEach((e) => toast.error(e));
      return;
    }

    setSaving(true);
    try {
      if (isEdit && id) {
        await updateAnnouncement(id, payload);
        toast.success('Announcement updated');
      } else {
        const doc = await createAnnouncement(payload);
        toast.success('Announcement created');
        navigate(`/announcements/${doc._id}/edit`, { replace: true });
      }
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const insertTemplate = (templateKey: string) => {
    const template = PAGE_TEMPLATES[templateKey];
    if (!template) return;
    try {
      const current = JSON.parse(pagesJson);
      if (!Array.isArray(current)) return;
      current.push(template.page);
      setPagesJson(JSON.stringify(current, null, 2));
      toast.success(`Added "${template.label}" template`);
    } catch {
      setPagesJson(JSON.stringify([template.page], null, 2));
    }
  };

  const formatJson = () => {
    try {
      const parsed = JSON.parse(pagesJson);
      setPagesJson(JSON.stringify(parsed, null, 2));
    } catch {}
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="flex items-center justify-center h-96">
          <div className="text-gray-400">Loading...</div>
        </div>
      </div>
    );
  }

  const pageCount = (() => {
    try { return JSON.parse(pagesJson).length; } catch { return 0; }
  })();

  const widgetCount = (() => {
    try {
      const pages = JSON.parse(pagesJson);
      const countWidgets = (w: any): number => 1 + (w.children || []).reduce((s: number, c: any) => s + countWidgets(c), 0);
      return pages.reduce((s: number, p: any) => s + (p.widgets || []).reduce((ws: number, w: any) => ws + countWidgets(w), 0), 0);
    } catch { return 0; }
  })();

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <button onClick={() => navigate('/announcements')} className="text-sm text-gray-500 hover:text-gray-700 mb-1">
              ← Back to Announcements
            </button>
            <h1 className="text-2xl font-bold text-gray-900">
              {isEdit ? 'Edit Announcement' : 'New Announcement'}
            </h1>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => handleSave()}
              disabled={saving}
              className="px-4 py-2 bg-gray-800 text-white text-sm font-semibold rounded-lg hover:bg-gray-700 disabled:opacity-50 transition"
            >
              {saving ? 'Saving...' : 'Save Draft'}
            </button>
            {isEdit && status !== 'ACTIVE' && (
              <button
                onClick={() => handleSave('ACTIVE')}
                disabled={saving}
                className="px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-600 text-white text-sm font-semibold rounded-lg hover:opacity-90 disabled:opacity-50 transition"
              >
                Publish
              </button>
            )}
            {isEdit && status === 'ACTIVE' && (
              <button
                onClick={() => handleSave('DRAFT')}
                disabled={saving}
                className="px-4 py-2 bg-yellow-500 text-white text-sm font-semibold rounded-lg hover:bg-yellow-600 disabled:opacity-50 transition"
              >
                Unpublish
              </button>
            )}
          </div>
        </div>

        {/* Stats bar */}
        {jsonValid && (
          <div className="flex gap-4 mb-4 text-sm">
            <span className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full">{pageCount} page{pageCount !== 1 ? 's' : ''}</span>
            <span className="px-3 py-1 bg-purple-50 text-purple-700 rounded-full">{widgetCount} widget{widgetCount !== 1 ? 's' : ''}</span>
            {isEdit && <span className={`px-3 py-1 rounded-full ${status === 'ACTIVE' ? 'bg-green-50 text-green-700' : status === 'ARCHIVED' ? 'bg-red-50 text-red-700' : 'bg-gray-100 text-gray-600'}`}>{status}</span>}
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 mb-4 border-b">
          {(['metadata', 'pages', 'theme', 'event-config', ...(isEdit ? ['analytics'] : [])] as const).map((tab) => {
            const label = tab === 'event-config' ? 'Event Config' : tab.charAt(0).toUpperCase() + tab.slice(1);
            return (
              <button
                key={tab}
                onClick={() => setActiveTab(tab as any)}
                className={`px-4 py-2 text-sm font-medium border-b-2 transition ${activeTab === tab ? 'border-[#F42026] text-[#F42026]' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
              >
                {label}
              </button>
            );
          })}
        </div>

        {/* Metadata Tab */}
        {activeTab === 'metadata' && (
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                <input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  onBlur={autoSlug}
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                  placeholder="Samurai Warrior: Path of Honor"
                  maxLength={200}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Slug *</label>
                <input
                  value={slug}
                  onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                  className="w-full px-3 py-2 border rounded-lg text-sm font-mono"
                  placeholder="samurai-warrior-path-of-honor"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Subtitle</label>
                <input
                  value={subtitle}
                  onChange={(e) => setSubtitle(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                  placeholder="Complete the event, unlock rewards, and become a legend."
                  maxLength={500}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Thumbnail URL *</label>
                <input
                  value={thumbnailUrl}
                  onChange={(e) => setThumbnailUrl(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                  placeholder="https://cdn.tezda.com/campaigns/..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Badge Text</label>
                <input
                  value={badgeText}
                  onChange={(e) => setBadgeText(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                  placeholder="EVENT"
                  maxLength={20}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type *</label>
                <select value={type} onChange={(e) => setType(e.target.value as AnnouncementType)} className="w-full px-3 py-2 border rounded-lg text-sm">
                  {ANNOUNCEMENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Audience</label>
                <select value={targetAudience} onChange={(e) => setTargetAudience(e.target.value as AnnouncementAudience)} className="w-full px-3 py-2 border rounded-lg text-sm">
                  {AUDIENCE_OPTIONS.map((a) => <option key={a} value={a}>{a}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Priority (0-100)</label>
                <input
                  type="number"
                  value={priority}
                  onChange={(e) => setPriority(Math.max(0, Math.min(100, parseInt(e.target.value) || 0)))}
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                  min={0} max={100}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Platform</label>
                <select
                  value={targetPlatforms[0] || 'all'}
                  onChange={(e) => setTargetPlatforms([e.target.value as Platform])}
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                >
                  {PLATFORM_OPTIONS.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Start Date *</label>
                <input
                  type="datetime-local"
                  value={startAt}
                  onChange={(e) => setStartAt(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Expires At</label>
                <input
                  type="datetime-local"
                  value={expiresAt}
                  onChange={(e) => setExpiresAt(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                />
              </div>
            </div>
          </div>
        )}

        {/* Pages Tab */}
        {activeTab === 'pages' && (
          <div className="bg-white rounded-xl shadow-sm border p-6">
            {/* Toolbar */}
            <div className="flex items-center gap-2 mb-4">
              <span className="text-sm font-medium text-gray-700 mr-2">Add template:</span>
              {Object.entries(PAGE_TEMPLATES).map(([key, tmpl]) => (
                <button
                  key={key}
                  onClick={() => insertTemplate(key)}
                  className="px-3 py-1.5 text-xs font-medium bg-gray-100 hover:bg-gray-200 rounded-lg transition"
                >
                  + {tmpl.label}
                </button>
              ))}
              <button
                onClick={() => insertTemplate('hero')}
                className="px-3 py-1.5 text-xs font-medium bg-blue-50 text-blue-700 hover:bg-blue-100 rounded-lg transition ml-auto"
                title="Add a blank page"
              >
                + Blank Page
              </button>
              <button onClick={formatJson} className="px-3 py-1.5 text-xs font-medium bg-purple-50 text-purple-700 hover:bg-purple-100 rounded-lg transition">
                Format JSON
              </button>
            </div>

            {/* Widget type reference */}
            <details className="mb-4">
              <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-700">Available widget types ({WIDGET_TYPES.length})</summary>
              <div className="mt-2 flex flex-wrap gap-1">
                {WIDGET_TYPES.map((w) => (
                  <span key={w} className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded font-mono">{w}</span>
                ))}
              </div>
            </details>

            {/* JSON Editor */}
            <div className="relative">
              <textarea
                ref={textareaRef}
                value={pagesJson}
                onChange={(e) => setPagesJson(e.target.value)}
                className={`w-full h-[600px] px-4 py-3 border rounded-lg font-mono text-xs leading-5 resize-none focus:outline-none focus:ring-2 ${jsonError ? 'border-red-300 focus:ring-red-300 bg-red-50/30' : 'border-gray-300 focus:ring-blue-300'}`}
                spellCheck={false}
                placeholder='[{ "backgroundColor": "#0A0A0A", "scrollable": true, "widgets": [...] }]'
              />
              {jsonError && (
                <div className="mt-2 px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-xs text-red-600">
                  {jsonError}
                </div>
              )}
              {jsonValid && !jsonError && (
                <div className="mt-2 px-3 py-2 bg-green-50 border border-green-200 rounded-lg text-xs text-green-600">
                  Valid — {pageCount} page{pageCount !== 1 ? 's' : ''}, {widgetCount} widget{widgetCount !== 1 ? 's' : ''}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Theme Tab */}
        {activeTab === 'theme' && (
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <p className="text-sm text-gray-500 mb-4">Theme colors are shared across all pages. The app uses these for consistent styling.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-xl">
              {[
                { label: 'Primary Color', value: themePrimary, set: setThemePrimary },
                { label: 'Secondary Color', value: themeSecondary, set: setThemeSecondary },
                { label: 'Text Color', value: themeText, set: setThemeText },
                { label: 'Background Color', value: themeBg, set: setThemeBg },
              ].map(({ label, value, set }) => (
                <div key={label} className="flex items-center gap-3">
                  <input
                    type="color"
                    value={value}
                    onChange={(e) => set(e.target.value)}
                    className="w-10 h-10 rounded-lg border cursor-pointer"
                  />
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700">{label}</label>
                    <input
                      value={value}
                      onChange={(e) => set(e.target.value)}
                      className="w-full px-2 py-1 border rounded text-xs font-mono mt-1"
                    />
                  </div>
                </div>
              ))}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Font Family</label>
                <input
                  value={themeFont}
                  onChange={(e) => setThemeFont(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                  placeholder="Poppins"
                />
              </div>
            </div>

            {/* Preview swatch */}
            <div className="mt-6 p-6 rounded-xl border" style={{ backgroundColor: themeBg }}>
              <div style={{ color: themePrimary, fontFamily: themeFont, fontSize: 24, fontWeight: 600, textAlign: 'center' }}>
                Preview Title
              </div>
              <div style={{ color: themeText, fontFamily: themeFont, fontSize: 14, textAlign: 'center', marginTop: 8 }}>
                Preview body text with theme colors applied
              </div>
              <div style={{ color: themeSecondary, fontFamily: themeFont, fontSize: 12, textAlign: 'center', marginTop: 4 }}>
                Secondary text
              </div>
            </div>
          </div>
        )}

        {/* Event Config Tab */}
        {activeTab === 'event-config' && (
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <div className="flex items-center gap-3 mb-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={ecEnabled}
                  onChange={(e) => setEcEnabled(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300"
                />
                <span className="text-sm font-medium text-gray-700">Enable dynamic event page</span>
              </label>
              <span className="text-xs text-gray-400">When enabled, the app can fetch a dynamic SDUI page with live wallet data</span>
            </div>

            {ecEnabled && (
              <div className="space-y-6">
                {/* Category Key */}
                <div className="max-w-md">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category Key *</label>
                  <input
                    value={ecCategoryKey}
                    onChange={(e) => setEcCategoryKey(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg text-sm font-mono"
                    placeholder="samurai-warrior"
                  />
                  <p className="text-xs text-gray-400 mt-1">Must match the wallet shop product category</p>
                </div>

                {/* Manage Assets link */}
                {isEdit && ecCategoryKey && (
                  <div className="flex items-center gap-3 p-3 bg-orange-50 border border-orange-200 rounded-lg max-w-md">
                    <svg className="w-4 h-4 text-orange-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-orange-700">Upload event assets in Tezda Assets</p>
                      <p className="text-xs text-orange-500 mt-0.5">Thumbnails, badges, milestones, and gift animations for <span className="font-mono">{ecCategoryKey}</span></p>
                    </div>
                    <a
                      href={`${process.env.REACT_APP_ASSETS_URL || 'https://tezda-assets.pages.dev'}/announcements/${id}/assets`}
                      target="_blank"
                      rel="noreferrer"
                      className="shrink-0 px-3 py-1.5 text-xs font-semibold rounded-lg bg-orange-500 text-white hover:bg-orange-600 transition whitespace-nowrap"
                    >
                      Manage Assets →
                    </a>
                  </div>
                )}

                {/* Milestones */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <label className="text-sm font-medium text-gray-700">Milestones</label>
                    <button
                      onClick={() => setEcMilestones([...ecMilestones, { value: 0, label: '', imageUrl: '' }])}
                      className="px-3 py-1.5 text-xs font-medium bg-blue-50 text-blue-700 hover:bg-blue-100 rounded-lg transition"
                    >
                      + Add Milestone
                    </button>
                  </div>
                  {ecMilestones.length === 0 && (
                    <p className="text-xs text-gray-400">No milestones configured. Add milestones to show progress tiers in the event.</p>
                  )}
                  <div className="space-y-3">
                    {ecMilestones.map((ms, i) => (
                      <div key={i} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                        <div className="flex-1 grid grid-cols-3 gap-3">
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">Value</label>
                            <input
                              type="number"
                              value={ms.value}
                              onChange={(e) => {
                                const updated = [...ecMilestones];
                                updated[i] = { ...ms, value: parseInt(e.target.value) || 0 };
                                setEcMilestones(updated);
                              }}
                              className="w-full px-2 py-1.5 border rounded text-sm"
                              min={0}
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">Label</label>
                            <input
                              value={ms.label}
                              onChange={(e) => {
                                const updated = [...ecMilestones];
                                updated[i] = { ...ms, label: e.target.value };
                                setEcMilestones(updated);
                              }}
                              className="w-full px-2 py-1.5 border rounded text-sm"
                              placeholder="Bronze Tier"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">Image URL</label>
                            <input
                              value={ms.imageUrl}
                              onChange={(e) => {
                                const updated = [...ecMilestones];
                                updated[i] = { ...ms, imageUrl: e.target.value };
                                setEcMilestones(updated);
                              }}
                              className="w-full px-2 py-1.5 border rounded text-sm"
                              placeholder="https://cdn.tezda.com/..."
                            />
                          </div>
                        </div>
                        <button
                          onClick={() => setEcMilestones(ecMilestones.filter((_, j) => j !== i))}
                          className="mt-5 px-2 py-1 text-xs text-red-500 hover:bg-red-50 rounded transition"
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Badge */}
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-3 block">Badge (awarded on completion)</label>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-2xl">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Badge Name</label>
                      <input
                        value={ecBadgeName}
                        onChange={(e) => setEcBadgeName(e.target.value)}
                        className="w-full px-3 py-2 border rounded-lg text-sm"
                        placeholder="Bushido Master"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Badge Image URL</label>
                      <input
                        value={ecBadgeImageUrl}
                        onChange={(e) => setEcBadgeImageUrl(e.target.value)}
                        className="w-full px-3 py-2 border rounded-lg text-sm"
                        placeholder="https://cdn.tezda.com/badges/..."
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Description</label>
                      <input
                        value={ecBadgeDescription}
                        onChange={(e) => setEcBadgeDescription(e.target.value)}
                        className="w-full px-3 py-2 border rounded-lg text-sm"
                        placeholder="Awarded for completing the Samurai event"
                        maxLength={500}
                      />
                    </div>
                  </div>
                  {ecBadgeImageUrl && (
                    <div className="mt-3 flex items-center gap-3">
                      <img src={ecBadgeImageUrl} alt="Badge preview" className="w-16 h-16 rounded-lg object-contain bg-gray-100 border" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                      <span className="text-sm text-gray-600">{ecBadgeName || 'Untitled badge'}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Analytics Tab */}
        {activeTab === 'analytics' && isEdit && (
          <div className="bg-white rounded-xl shadow-sm border p-6">
            {analytics === null ? (
              <div className="text-gray-400 text-center py-8">Loading analytics...</div>
            ) : analytics.length === 0 ? (
              <div className="text-gray-400 text-center py-8">No analytics data yet</div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {analytics.map((stat) => (
                  <div key={stat.eventType} className="p-4 bg-gray-50 rounded-xl">
                    <div className="text-2xl font-bold text-gray-900">{stat.count}</div>
                    <div className="text-sm text-gray-500">{stat.eventType.replace('_', ' ')}</div>
                    <div className="text-xs text-gray-400 mt-1">{stat.uniqueUsers} unique users</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AnnouncementEditor;
